import React from "react";
import { useDragLayer, XYCoord } from "react-dnd";
import { ItemTypes } from "../../constants";
import { getFullMediaUrl } from "../../utils/mediaUtils";

// Full-screen, click-through overlay that hosts the custom drag preview.
const layerStyles: React.CSSProperties = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 1000,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

const getItemTransform = (offset: XYCoord | null): React.CSSProperties => {
  if (!offset) {
    return { display: "none" };
  }
  return { transform: `translate(${offset.x}px, ${offset.y}px)` };
};

// Best-effort thumbnail for a creative (prefers a real thumbnail, falls back
// to the file itself for images).
const getCreativeThumb = (creative: any): string | null => {
  const media = creative?.media?.[0];
  if (!media) return null;
  return (
    getFullMediaUrl(media.thumbnailUrl) ||
    getFullMediaUrl(media.fileUrl) ||
    null
  );
};

const CustomDragLayer: React.FC = () => {
  const { itemType, isDragging, item, currentOffset } = useDragLayer(
    (monitor) => ({
      item: monitor.getItem() as any,
      itemType: monitor.getItemType(),
      currentOffset: monitor.getSourceClientOffset(),
      isDragging: monitor.isDragging(),
    }),
  );

  // Only render for library drags (creatives / playlists), which carry an
  // `items` array. Other drag types (e.g. in-playlist reordering) are ignored.
  const isLibraryDrag =
    itemType === ItemTypes.CREATIVE || itemType === ItemTypes.PLAYLIST;
  const items: any[] = Array.isArray(item?.items) ? item.items : [];

  if (!isDragging || !isLibraryDrag || items.length === 0) {
    return null;
  }

  const count = items.length;
  const first = items[0];
  const isCreative = itemType === ItemTypes.CREATIVE;
  const thumb = isCreative ? getCreativeThumb(first) : null;
  const label = isCreative
    ? first?.media?.[0]?.fileName || first?.uniqueId || "Creative"
    : first?.name || "Playlist";
  const fallbackIcon = isCreative
    ? "assets/images/creative-icon.png"
    : "assets/images/playlist-icon.jpg";

  return (
    <div style={layerStyles}>
      <div style={getItemTransform(currentOffset)}>
        <div className="relative w-24">
          {/* Stacked cards behind to hint at multiple items */}
          {count > 1 && (
            <>
              <div className="absolute left-2 top-2 h-full w-full rounded-lg border bg-white shadow-md" />
              <div className="absolute left-1 top-1 h-full w-full rounded-lg border bg-white shadow-md" />
            </>
          )}
          <div className="relative flex items-center gap-2 rounded-lg border bg-white p-2 shadow-lg">
            <div className="h-20 w-20">
              <img
                src={thumb || fallbackIcon}
                alt={label}
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = fallbackIcon;
                }}
              />
            </div>
            {/* <span className="truncate text-sm font-medium text-gray-700">
              {label}
            </span> */}
          </div>
          {count > 1 && (
            <span className="absolute -right-2 -top-2 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-blue-500 px-1.5 text-xs font-bold text-white shadow">
              {count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomDragLayer;
